import React, { useEffect, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { useOrg } from '../../../hooks/useOrg';
import { api } from '../../../lib/api';
import OfficeHoursEditor from '../../locations/OfficeHoursEditor';

// Practice hours, per office. Availability belongs to the location, not
// the person: set when each office is open and every provider working
// there inherits it. The same editor appears inline in the referral inbox
// when a clinic accepts a referral for an office with no hours yet.

export default function ProviderHoursTab() {
  const { currentOrg, currentRole } = useOrg();
  const canEdit = !!currentRole && currentRole !== 'client';
  const [locations, setLocations] = useState(null);
  const [locationId, setLocationId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentOrg?.id) return;
    api.getLocations(currentOrg.id)
      .then((locs) => {
        const live = (locs || []).filter((l) => !l.archived_at);
        setLocations(live);
        setLocationId((id) => id || live[0]?.id || '');
      })
      .catch((e) => { setError(e.message); setLocations([]); });
  }, [currentOrg?.id]);

  if (locations === null) return <p className="text-[13px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading offices…</p>;
  if (locations.length === 0) {
    return (
      <div className="bg-elevated border border-theme rounded-[14px] p-6 text-center">
        <MapPin size={20} className="text-muted-c mx-auto mb-2" />
        <div className="text-[14px] font-semibold text-primary-c">No locations yet</div>
        <p className="text-[12.5px] text-muted-c mt-1">Add your office under Locations first; its hours are set here.</p>
      </div>
    );
  }
  const office = locations.find((l) => l.id === locationId);

  return (
    <div className="space-y-5">
      <div className="bg-elevated border border-theme rounded-[14px] p-5">
        <div className="mb-4">
          <h2 className="text-[15px] font-bold text-primary-c">Practice hours</h2>
          <p className="text-[12.5px] text-muted-c mt-0.5 max-w-[560px]">
            When each office is open. Open times for booking — your calendar, the patient portal and referral booking — come from these hours, and every provider at the office follows them. A day left unchecked has no slots.
          </p>
        </div>

        {error && <div className="text-[12px] text-danger bg-danger-soft rounded-[10px] px-3.5 py-2.5 mb-4">{error}</div>}

        {locations.length > 1 && (
          <div className="flex gap-0.5 bg-tertiary p-1 rounded-[10px] mb-4 overflow-x-auto">
            {locations.map((l) => (
              <button key={l.id} onClick={() => setLocationId(l.id)} className={`shrink-0 px-3 py-[6px] rounded-[7px] text-[12px] font-semibold transition-all inline-flex items-center gap-1.5 ${locationId === l.id ? 'bg-accent text-white shadow-glow' : 'text-muted-c hover:text-primary-c'}`}>
                <MapPin size={11} /> {l.name}
              </button>
            ))}
          </div>
        )}

        {locationId && <OfficeHoursEditor key={locationId} orgId={currentOrg.id} locationId={locationId} officeName={office?.name} canEdit={canEdit} />}
      </div>
    </div>
  );
}
